import { formatQty } from '@shared/quantity';
import { fetchProductCombos } from '../../catalog/public-api';
import { getEffectiveStockSalePolicy } from '../../inventory/public-api';
import { getAvailability } from '../../inventory/stock/infra/operations/getAvailability';
import { supabase } from '../../shared/supabase/supabaseClient';
import type {
  FiscalBuyerSyncFields,
  FiscalMode,
  SaleDiscountInput,
  SaleItemInput,
  SalePaymentInput,
  SaleStatus,
} from './salesRepoTypes';

export type {
  FiscalBuyerSyncFields,
  FiscalMode,
  SaleDiscountInput,
  SaleItemInput,
  SalePaymentInput,
  SaleStatus,
} from './salesRepoTypes';

export { computeSalePaidPendingAmounts } from '../../shared/numeric/public-api';

export { executeSaleIntake as createSale } from '../application/saleIntake';
export { executeSaleVoidRemoteIntake as voidSale } from '../application/saleVoidIntake';
export type { CreateSaleResult, VoidSaleResult } from './salesRepoMutations';

export type StockWarningDetail = {
  productId: string;
  /** #706: presente cuando el aviso corresponde a una variante del producto. */
  variantId?: string | null;
  name: string;
  sku?: string;
  requested: number;
  available: number;
  sellable: number;
  expiredUnits: number;
  /** 'insufficient' | 'expired_stock' | 'no_stock' */
  warningType: 'insufficient' | 'expired_stock' | 'no_stock';
  isBlocking: boolean;
  message: string;
};

export type PaymentMethodRow = {
  id: SalePaymentInput['methodId'];
  name: string;
  kind: string;
  allowsSplit: boolean;
  isDigital: boolean;
};

const shouldAffectStock = (item: SaleItemInput) => item.affectsStock !== false;

async function comboLimitHint(branchId: string, productId: string): Promise<string> {
  const combos = await fetchProductCombos([productId]);
  if (!combos.has(productId)) return '';
  const { data, error } = await supabase.rpc('catalog_combo_limiting_component_name', {
    p_branch_id: branchId,
    p_combo_product_id: productId,
  });
  if (error) return '';
  const name = typeof data === 'string' ? data.trim() : '';
  return name ? ` Limitado por ${name}.` : '';
}

const DEFAULT_WEB_PAYMENT_METHODS: PaymentMethodRow[] = [
  { id: 'cash', name: 'Efectivo', kind: 'cash', allowsSplit: true, isDigital: false },
  { id: 'transfer', name: 'Transferencia', kind: 'bank', allowsSplit: true, isDigital: true },
  { id: 'card', name: 'Tarjeta', kind: 'card', allowsSplit: true, isDigital: true },
  { id: 'mercadopago_qr', name: 'Mercado Pago QR', kind: 'digital', allowsSplit: true, isDigital: true },
  { id: 'cheque', name: 'Cheque', kind: 'check', allowsSplit: true, isDigital: false },
  { id: 'fiado', name: 'A cuenta', kind: 'credit', allowsSplit: false, isDigital: false },
];

export const getPaymentMethods = async (): Promise<PaymentMethodRow[]> => {
    return DEFAULT_WEB_PAYMENT_METHODS;
};

export type StockWarningsResult = {
  canProceed: boolean;
  warnings: string[];
  details: StockWarningDetail[];
};

export const getStockWarnings = async (
  branchId: string,
  items: SaleItemInput[]
): Promise<StockWarningsResult> => {
  const policy = getEffectiveStockSalePolicy(branchId);
  const trackedItems = items.filter(shouldAffectStock);
  const warnings: string[] = [];
  const details: StockWarningDetail[] = [];

  for (const item of trackedItems) {
    const { quantity: totalAvailable, sellable: effectiveAvailable, expiredUnits } =
      await getAvailability(branchId, item.productId, item.variantId);

    if (effectiveAvailable < item.quantity) {
      let msg: string;
      let warningType: StockWarningDetail['warningType'];

      const uom = item.unitOfMeasure;
      if (totalAvailable === 0) {
        msg = `${item.name}: sin stock cargado en esta sucursal`;
        warningType = 'no_stock';
      } else if (expiredUnits > 0 && totalAvailable >= item.quantity) {
        msg = `${item.name}: ${formatQty(expiredUnits, uom)} vencidas. Disponible para venta: ${formatQty(effectiveAvailable, uom)}, pedido: ${formatQty(item.quantity, uom)}.`;
        warningType = 'expired_stock';
      } else {
        msg = `${item.name}: pedido ${formatQty(item.quantity, uom)}, disponible ${formatQty(effectiveAvailable, uom)}.`;
        warningType = 'insufficient';
      }

      msg += await comboLimitHint(branchId, item.productId);

      warnings.push(msg);
      details.push({
        productId: item.productId,
        variantId: item.variantId ?? null,
        name: item.name,
        sku: item.sku,
        requested: item.quantity,
        available: totalAvailable,
        sellable: effectiveAvailable,
        expiredUnits,
        warningType,
        isBlocking: effectiveAvailable === 0,
        message: msg,
      });
    }

    // Linked product stock warnings from menu options
    if (item.options?.length) {
      for (const opt of item.options) {
        if (!opt.linkedProductId) continue;
        const linkedQty = (opt.qty || 1) * item.quantity;
        const { quantity: lTotal, sellable: lSellable, expiredUnits: lExpired } =
          await getAvailability(branchId, opt.linkedProductId, null);
        if (lSellable < linkedQty) {
          const lName = opt.choiceName || 'Insumo';
          const lUom = item.unitOfMeasure;
          let lMsg: string;
          let lType: StockWarningDetail['warningType'];
          if (lTotal === 0) {
            lMsg = `${lName} (insumo): sin stock cargado en esta sucursal`;
            lType = 'no_stock';
          } else if (lExpired > 0 && lTotal >= linkedQty) {
            lMsg = `${lName} (insumo): ${formatQty(lExpired, lUom)} vencidas. Disponible: ${formatQty(lSellable, lUom)}, pedido: ${formatQty(linkedQty, lUom)}.`;
            lType = 'expired_stock';
          } else {
            lMsg = `${lName} (insumo): pedido ${formatQty(linkedQty, lUom)}, disponible ${formatQty(lSellable, lUom)}.`;
            lType = 'insufficient';
          }
          warnings.push(lMsg);
          details.push({
            productId: opt.linkedProductId,
            name: lName,
            sku: undefined,
            requested: linkedQty,
            available: lTotal,
            sellable: lSellable,
            expiredUnits: lExpired,
            warningType: lType,
            isBlocking: lSellable === 0,
            message: lMsg,
          });
        }
      }
    }
  }

  const canProceed = policy === 'block' ? warnings.length === 0 : true;
  return { canProceed, warnings, details };
};

export {
  getTodaySalesSnapshot,
  getSalesSnapshotByDateRange,
  getSalesSnapshotByDateRangeWithVentasScope,
  getSalesTimeBandWindows,
  getSalesByTimeBand,
  getSalesComparison,
  getPromotionPerformanceByDateRange,
  CASHIER_LIST_FILTER_NULL,
  PromotionPerformanceUnavailableError,
  PROMOTION_PERFORMANCE_UNAVAILABLE_CODE,
  type TimeBand,
  type SalesByTimeBandRow,
  type SalesComparison,
  type SalesByCashierRow,
  type SalesSnapshot,
  type PromotionPerformanceRow,
  type PromotionPerformanceSnapshot,
} from './salesRepoSnapshots';

export {
  listSales,
  countSales,
  parsePaymentMethodIds,
  type SaleListRow,
  type SalesListFilterParams,
} from './salesRepoList';

export {
  getSaleDetail,
  getSalePaymentTotals,
  getCustomerNameById,
  type SaleDetail,
  type SaleDetailItem,
  type SaleDetailDiscount,
  type SaleDetailPayment,
} from './salesRepoDetail';
